import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Crown, Snowflake, CalendarPlus, LogOut, FlaskConical, Mail, ScrollText,
  UserPlus, Loader2, Trash2, Sparkles, Award, Swords, Pause, Play, Hourglass,
} from "lucide-react";
import { PosterStripes, PosterRibbon, PosterGrain } from "@/components/PosterFX";

/**
 * Platform-admin power-tools drawer for a single company.
 * Wraps all admin-only endpoints (grant tier, freeze, extend trial, force logout,
 * toggle demo, send email, audit log, change owner, grant badge/frame) in one
 * tabbed dialog so the operator can jump between actions without leaving the row.
 */
export default function CompanyManageDialog({ company, open, onClose, onChange }) {
  const accent = "#E13C32";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent
        className="text-white max-w-3xl max-h-[90vh] overflow-y-auto p-0 border-2"
        style={{
          backgroundColor: "#0A0A0A",
          borderColor: `${accent}bb`,
          backgroundImage: `linear-gradient(160deg, ${accent}22, transparent 55%)`,
          boxShadow: `0 0 30px ${accent}55, inset 0 0 24px rgba(0,0,0,0.55)`,
        }}
        data-testid={`manage-dialog-${company.id}`}
      >
        <PosterStripes color={accent} size={104} opacity={0.85} />
        <PosterGrain opacity={0.06} />
        <PosterRibbon color={accent} size="sm">★ COMPANY DOSSIER ★</PosterRibbon>
        <div className="relative p-4 pt-6 sm:p-5 sm:pt-7">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">MANAGE · {company.name}</DialogTitle>
          <DialogDescription className="text-[#A1A1AA] text-xs">
            Platform-admin actions. Every change is audit-logged.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="tier" className="mt-2">
          <TabsList className="bg-[#121212] border border-[#27272A] p-1 rounded-sm h-auto flex-wrap">
            <TabsTrigger value="tier" className="data-[state=active]:bg-[#E13C32]/10 data-[state=active]:text-[#E46B5A]" data-testid="tab-tier">Tier</TabsTrigger>
            <TabsTrigger value="account" className="data-[state=active]:bg-[#E13C32]/10 data-[state=active]:text-[#E46B5A]" data-testid="tab-account">Account</TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#E13C32]/10 data-[state=active]:text-[#E46B5A]" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="outreach" className="data-[state=active]:bg-[#E13C32]/10 data-[state=active]:text-[#E46B5A]" data-testid="tab-outreach">Outreach</TabsTrigger>
            <TabsTrigger value="arena" className="data-[state=active]:bg-[#E13C32]/10 data-[state=active]:text-[#E46B5A]" data-testid="tab-arena">Arena</TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-[#E13C32]/10 data-[state=active]:text-[#E46B5A]" data-testid="tab-audit">Audit</TabsTrigger>
          </TabsList>

          <TabsContent value="tier" className="pt-4">
            <TierSection company={company} onChange={onChange} />
          </TabsContent>
          <TabsContent value="account" className="pt-4 space-y-6">
            <FreezeSection company={company} onChange={onChange} />
            <TrialSection company={company} onChange={onChange} />
            <DemoSection company={company} onChange={onChange} />
            <ForceLogoutSection company={company} onChange={onChange} />
          </TabsContent>
          <TabsContent value="users" className="pt-4 space-y-6">
            <ChangeOwnerSection company={company} onChange={onChange} />
            <GrantBadgeSection company={company} />
            <GrantFrameSection company={company} />
          </TabsContent>
          <TabsContent value="outreach" className="pt-4 space-y-6">
            <TrialReminderSection company={company} />
            <SendEmailSection company={company} />
          </TabsContent>
          <TabsContent value="arena" className="pt-4">
            <ChallengesSection company={company} />
          </TabsContent>
          <TabsContent value="audit" className="pt-4">
            <AuditLogSection company={company} />
          </TabsContent>
        </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------- TIER GRANT ------------------------------- */
function TierSection({ company, onChange }) {
  const [planId, setPlanId] = useState("premium");
  const [days, setDays] = useState(14);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    setCurrent({
      granted_plan: company.granted_plan,
      granted_plan_expires_at: company.granted_plan_expires_at,
    });
  }, [company]);

  const grant = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/companies/${company.id}/grant-plan`, { plan_id: planId, days: parseInt(days, 10) });
      toast.success(`Granted ${planId.toUpperCase()} for ${days} days`);
      setCurrent({ granted_plan: r.data.granted_plan, granted_plan_expires_at: r.data.expires_at });
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this company's granted tier?")) return;
    try {
      await api.delete(`/admin/companies/${company.id}/grant-plan`);
      setCurrent({ granted_plan: null, granted_plan_expires_at: null });
      toast.success("Grant revoked");
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeGrant = current?.granted_plan && current?.granted_plan_expires_at
    && new Date(current.granted_plan_expires_at) > new Date();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Crown className="w-4 h-4 text-[#FFC832]" />
        <div className="font-label text-xs tracking-wider text-white/70">TEMPORARY TIER GRANT</div>
      </div>
      {activeGrant ? (
        <div className="p-3 bg-[#FFC832]/10 border border-[#FFC832]/40 rounded-sm text-sm" data-testid="active-grant">
          Currently on <strong>{current.granted_plan.toUpperCase()}</strong> until{" "}
          {new Date(current.granted_plan_expires_at).toLocaleString()}.
          <Button size="sm" onClick={revoke} className="ml-3 bg-[#E13C32] hover:bg-[#EB5A50] text-white h-7 rounded-sm text-[11px]" data-testid="revoke-grant">
            <Trash2 className="w-3 h-3 mr-1" /> Revoke
          </Button>
        </div>
      ) : null}
      <div className="grid grid-cols-3 gap-3 items-end">
        <div>
          <Label className="text-xs text-[#A1A1AA]">Plan</Label>
          <Select value={planId} onValueChange={setPlanId}>
            <SelectTrigger className="mt-1 bg-[#121212] border-[#27272A]" data-testid="grant-plan-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#27272A] text-white">
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="premium">Premium</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-[#A1A1AA]">Days (1–90)</Label>
          <Input type="number" min="1" max="90" value={days} onChange={(e) => setDays(e.target.value)}
                 className="mt-1 bg-[#121212] border-[#27272A]" data-testid="grant-days-input" />
        </div>
        <Button onClick={grant} disabled={saving} className="bg-[#FFC832] hover:bg-[#FFC832] text-black font-label rounded-sm h-10" data-testid="grant-plan-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Grant tier"}
        </Button>
      </div>
      <p className="text-[11px] text-[#A1A1AA]">
        Overlays their paid plan for the duration. Auto-expires. Their real Stripe plan isn't touched.
      </p>
    </div>
  );
}

/* -------------------------------- FREEZE ---------------------------------- */
function FreezeSection({ company, onChange }) {
  const [frozen, setFrozen] = useState(!!company.frozen);
  const [saving, setSaving] = useState(false);
  const toggle = async (v) => {
    setSaving(true);
    try {
      await api.post(`/admin/companies/${company.id}/freeze`, { frozen: v });
      setFrozen(v);
      toast.success(v ? "Account frozen (reads only)" : "Account unfrozen");
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center justify-between p-3 border border-[#27272A] rounded-sm bg-[#0A0A0A]">
      <div className="flex items-center gap-3">
        <Snowflake className="w-5 h-5 text-[#7EC4EA]" />
        <div>
          <div className="font-medium text-sm">Freeze account</div>
          <div className="text-[11px] text-[#A1A1AA]">Reps can still read. Writes (sales, chat, arena) return 423.</div>
        </div>
      </div>
      <Switch checked={frozen} onCheckedChange={toggle} disabled={saving} data-testid="freeze-switch" />
    </div>
  );
}

/* -------------------------------- TRIAL ---------------------------------- */
function TrialSection({ company, onChange }) {
  const [days, setDays] = useState(14);
  const [saving, setSaving] = useState(false);
  const extend = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/companies/${company.id}/extend-trial`, { days: parseInt(days, 10) });
      toast.success(`Trial extended to ${new Date(r.data.trial_ends_at).toLocaleDateString()}`);
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="p-3 border border-[#27272A] rounded-sm bg-[#0A0A0A]">
      <div className="flex items-center gap-3 mb-2">
        <CalendarPlus className="w-5 h-5 text-[#34C759]" />
        <div className="font-medium text-sm">Extend trial</div>
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label className="text-xs text-[#A1A1AA]">Days to add (1–180)</Label>
          <Input type="number" min="1" max="180" value={days} onChange={(e) => setDays(e.target.value)}
                 className="mt-1 bg-[#121212] border-[#27272A]" data-testid="extend-trial-days" />
        </div>
        <Button onClick={extend} disabled={saving} className="bg-[#34C759] hover:bg-[#7CFF5D] text-black font-label rounded-sm" data-testid="extend-trial-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Extend"}
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------- DEMO ---------------------------------- */
function DemoSection({ company, onChange }) {
  const [demo, setDemo] = useState(!!company.is_demo);
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/companies/${company.id}/toggle-demo`);
      setDemo(r.data.demo);
      toast.success(r.data.demo ? "Marked as demo (excluded from MRR)" : "Marked as real (counted in MRR)");
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center justify-between p-3 border border-[#27272A] rounded-sm bg-[#0A0A0A]">
      <div className="flex items-center gap-3">
        <FlaskConical className="w-5 h-5 text-[#FFC832]" />
        <div>
          <div className="font-medium text-sm">Mark as demo / test</div>
          <div className="text-[11px] text-[#A1A1AA]">Excludes company from MRR and platform stats.</div>
        </div>
      </div>
      <Switch checked={demo} onCheckedChange={toggle} disabled={saving} data-testid="demo-switch" />
    </div>
  );
}

/* ----------------------------- FORCE LOGOUT ------------------------------ */
function ForceLogoutSection({ company }) {
  const [saving, setSaving] = useState(false);
  const nuke = async () => {
    if (!window.confirm(`Force logout every user in ${company.name}? All active sessions will be invalidated immediately.`)) return;
    setSaving(true);
    try {
      await api.post(`/admin/companies/${company.id}/force-logout`);
      toast.success("All sessions revoked");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center justify-between p-3 border border-[#E13C32]/30 rounded-sm bg-[#E13C32]/5">
      <div className="flex items-center gap-3">
        <LogOut className="w-5 h-5 text-[#E13C32]" />
        <div>
          <div className="font-medium text-sm">Force logout all users</div>
          <div className="text-[11px] text-[#A1A1AA]">Security incident response. Every user must sign back in.</div>
        </div>
      </div>
      <Button onClick={nuke} disabled={saving} className="bg-[#E13C32] hover:bg-[#EB5A50] text-white font-label rounded-sm h-9" data-testid="force-logout-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kick everyone"}
      </Button>
    </div>
  );
}

/* ----------------------------- CHANGE OWNER ------------------------------ */
function ChangeOwnerSection({ company, onChange }) {
  const [users, setUsers] = useState([]);
  const [newOwnerId, setNewOwnerId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.post(`/admin/impersonate/${company.id}`).then(async () => {
      const r = await api.get("/employees");
      setUsers((r.data || []).filter((u) => u.active && u.id !== company.owner_user_id));
      // Stop impersonation immediately
      await api.post("/admin/stop-impersonation");
    }).catch(() => setUsers([]));
  }, [company.id, company.owner_user_id]);

  const transfer = async () => {
    if (!newOwnerId) { toast.error("Pick a user first"); return; }
    if (!window.confirm(`Transfer ownership of ${company.name}? Current owner will be demoted to admin.`)) return;
    setSaving(true);
    try {
      await api.post(`/admin/companies/${company.id}/change-owner`, { new_owner_user_id: newOwnerId });
      toast.success("Ownership transferred");
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="p-3 border border-[#27272A] rounded-sm bg-[#0A0A0A]">
      <div className="flex items-center gap-3 mb-2">
        <UserPlus className="w-5 h-5 text-[#178AE5]" />
        <div>
          <div className="font-medium text-sm">Change owner</div>
          <div className="text-[11px] text-[#A1A1AA]">Current owner demoted to admin. New owner gets full control.</div>
        </div>
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label className="text-xs text-[#A1A1AA]">New owner</Label>
          <Select value={newOwnerId} onValueChange={setNewOwnerId}>
            <SelectTrigger className="mt-1 bg-[#121212] border-[#27272A]" data-testid="new-owner-select">
              <SelectValue placeholder="Pick a user…" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#27272A] text-white">
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={transfer} disabled={saving || !newOwnerId} className="bg-[#178AE5] hover:bg-[#41A0E8] text-white font-label rounded-sm" data-testid="change-owner-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Transfer"}
        </Button>
      </div>
    </div>
  );
}

/* ---------------------------- GRANT BADGE ------------------------------ */
function GrantBadgeSection({ company }) {
  const [badgeId, setBadgeId] = useState("");
  const [userId, setUserId] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!badgeId.trim() || !userId.trim()) { toast.error("Both badge_id and user_id required"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/companies/${company.id}/grant-badge`, { badge_id: badgeId.trim(), user_id: userId.trim() });
      toast.success("Badge granted");
      setBadgeId(""); setUserId("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="p-3 border border-[#27272A] rounded-sm bg-[#0A0A0A]">
      <div className="flex items-center gap-3 mb-2">
        <Award className="w-5 h-5 text-[#FFC832]" />
        <div className="font-medium text-sm">Grant a badge to a user</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Input placeholder="badge_id" value={badgeId} onChange={(e) => setBadgeId(e.target.value)} className="bg-[#121212] border-[#27272A]" data-testid="grant-badge-id" />
        <Input placeholder="user_id" value={userId} onChange={(e) => setUserId(e.target.value)} className="bg-[#121212] border-[#27272A]" data-testid="grant-badge-user" />
        <Button onClick={submit} disabled={saving} className="bg-[#FFC832] hover:bg-[#FFC832] text-black font-label rounded-sm" data-testid="grant-badge-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Grant"}
        </Button>
      </div>
      <p className="text-[10px] text-[#A1A1AA] mt-1">Both IDs are UUIDs. Copy them from the audit log or the app's browser inspector.</p>
    </div>
  );
}

/* ---------------------------- GRANT FRAME ------------------------------ */
function GrantFrameSection({ company }) {
  const [frameId, setFrameId] = useState("gold_champion");
  const [userId, setUserId] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!userId.trim()) { toast.error("user_id required"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/companies/${company.id}/grant-frame`, { frame_id: frameId, user_id: userId.trim() });
      toast.success("Frame unlocked");
      setUserId("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="p-3 border border-[#27272A] rounded-sm bg-[#0A0A0A]">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="w-5 h-5 text-[#754ECA]" />
        <div className="font-medium text-sm">Grant a cosmetic frame</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Select value={frameId} onValueChange={setFrameId}>
          <SelectTrigger className="bg-[#121212] border-[#27272A]" data-testid="grant-frame-id"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-[#27272A] text-white">
            <SelectItem value="gold_champion">Gold Champion</SelectItem>
            <SelectItem value="lightning">Lightning</SelectItem>
            <SelectItem value="crimson">Crimson</SelectItem>
            <SelectItem value="platinum">Platinum</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="user_id" value={userId} onChange={(e) => setUserId(e.target.value)} className="bg-[#121212] border-[#27272A]" data-testid="grant-frame-user" />
        <Button onClick={submit} disabled={saving} className="bg-[#754ECA] hover:bg-[#8B4EFF] text-white font-label rounded-sm" data-testid="grant-frame-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Grant"}
        </Button>
      </div>
    </div>
  );
}

/* -------------------------- TRIAL REMINDER ------------------------------ */
function TrialReminderSection({ company }) {
  const [sending, setSending] = useState(false);
  const now = new Date();
  const grantActive = company.granted_plan && company.granted_plan_expires_at && new Date(company.granted_plan_expires_at) > now;
  const trialActive = !grantActive && company.trial_ends_at && new Date(company.trial_ends_at) > now;
  const endsAt = grantActive ? company.granted_plan_expires_at : trialActive ? company.trial_ends_at : null;
  const planLabel = grantActive ? company.granted_plan.toUpperCase() : "FREE TRIAL";

  const sendReminder = async () => {
    if (!window.confirm(`Send the "trial ending" reminder email to ${company.owner_email}?`)) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/companies/${company.id}/send-trial-reminder`);
      toast.success(`Reminder sent to ${r.data.sent_to} — ${r.data.days_left} day(s) left on ${r.data.plan_label}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  return (
    <div className="p-3 border border-[#FFC832]/30 rounded-sm bg-[#FFC832]/5" data-testid="trial-reminder-section">
      <div className="flex items-center gap-3 mb-2">
        <Hourglass className="w-5 h-5 text-[#FFC832]" />
        <div>
          <div className="font-medium text-sm">Trial ending reminder</div>
          <div className="text-[11px] text-[#A1A1AA]">
            One-click branded email nudging the owner to lock in a plan before their access expires.
          </div>
        </div>
      </div>
      {endsAt ? (
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-[#A1A1AA]">
            <span className="text-[#FFC832] font-medium">{planLabel}</span> access ends{" "}
            <span className="text-white">{new Date(endsAt).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
          </div>
          <Button onClick={sendReminder} disabled={sending}
            className="bg-[#FFC832] hover:bg-[#FFC832] text-black font-label rounded-sm h-9 flex-shrink-0"
            data-testid="send-trial-reminder-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reminder"}
          </Button>
        </div>
      ) : (
        <div className="text-xs text-[#A1A1AA]" data-testid="trial-reminder-inactive">
          No active trial or tier grant on this company — nothing to remind about.
        </div>
      )}
    </div>
  );
}

/* ------------------------------- EMAIL --------------------------------- */
function SendEmailSection({ company }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required"); return; }
    setSending(true);
    try {
      const r = await api.post(`/admin/companies/${company.id}/send-owner-email`, { subject: subject.trim(), body: body.trim() });
      toast.success(`Emailed ${r.data.sent_to}`);
      setSubject(""); setBody("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };
  return (
    <div className="p-3 border border-[#27272A] rounded-sm bg-[#0A0A0A]">
      <div className="flex items-center gap-3 mb-3">
        <Mail className="w-5 h-5 text-[#34C759]" />
        <div>
          <div className="font-medium text-sm">Email the company owner</div>
          <div className="text-[11px] text-[#A1A1AA]">Goes to <span className="font-mono">{company.owner_email}</span> via SalesArena's sending domain.</div>
        </div>
      </div>
      <Label className="text-xs text-[#A1A1AA]">Subject</Label>
      <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Just checking in!" className="mt-1 mb-2 bg-[#121212] border-[#27272A]" data-testid="email-subject" />
      <Label className="text-xs text-[#A1A1AA]">Body</Label>
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Write your message…" className="mt-1 mb-3 bg-[#121212] border-[#27272A] font-mono text-sm" data-testid="email-body" />
      <Button onClick={send} disabled={sending} className="bg-[#34C759] hover:bg-[#7CFF5D] text-black font-label rounded-sm" data-testid="email-send-btn">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send email"}
      </Button>
    </div>
  );
}

/* -------------------------------- AUDIT -------------------------------- */
/* ------------------------------ CHALLENGES ------------------------------- */
const CH_STATUS_COLORS = {
  active: "#34C759", pending: "#EAB132", completed: "#71717A",
  declined: "#E13C32", expired: "#71717A", cancelled: "#E13C32",
};

function ChallengesSection({ company }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null); // challenge id in-flight

  const load = () => {
    api.get(`/admin/companies/${company.id}/challenges`)
      .then((r) => setRows(r.data.challenges || []))
      .catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  };
  useEffect(load, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePause = async (ch) => {
    setBusy(ch.id);
    try {
      const r = await api.post(`/admin/challenges/${ch.id}/pause`, { paused: !ch.admin_paused });
      toast.success(r.data.paused ? "Challenge paused — scoring frozen" : "Challenge resumed");
      setRows((prev) => prev.map((c) => (c.id === ch.id ? { ...c, admin_paused: r.data.paused } : c)));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  if (rows === null) return <div className="p-6 text-center text-[#A1A1AA] text-sm">Loading challenges…</div>;
  if (rows.length === 0) return <div className="p-6 text-center text-[#A1A1AA] text-sm" data-testid="challenges-empty">No arena challenges for this company yet.</div>;

  return (
    <div className="space-y-3" data-testid="challenges-section">
      <div className="flex items-center gap-2 mb-1">
        <Swords className="w-4 h-4 text-[#E13C32]" />
        <div className="font-label text-xs tracking-wider text-white/70">ARENA CHALLENGES</div>
      </div>
      {rows.map((ch) => {
        const statusColor = CH_STATUS_COLORS[ch.status] || "#A1A1AA";
        const pausable = ch.status === "active" || ch.status === "pending";
        return (
          <div key={ch.id} className="flex items-center justify-between gap-3 p-3 border border-[#27272A] rounded-sm bg-[#0A0A0A]" data-testid={`challenge-row-${ch.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">
                {ch.challenger_name || "?"} <span className="text-[#A1A1AA]">vs</span> {ch.opponent_name || "?"}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded-sm border"
                      style={{ color: statusColor, borderColor: `${statusColor}55`, background: `${statusColor}1a` }}>
                  {ch.status}
                </span>
                {ch.admin_paused ? (
                  <span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded-sm border text-[#EAB132] border-[#EAB13255] bg-[#EAB1321a]" data-testid={`paused-chip-${ch.id}`}>
                    ⏸ Paused
                  </span>
                ) : null}
                <span className="text-[10px] text-[#71717A] uppercase tracking-wider">
                  {(ch.challenge_type || "").replaceAll("_", " ")} · {ch.kind || "duel"}
                </span>
                {ch.created_at ? <span className="text-[10px] text-[#71717A]">{new Date(ch.created_at).toLocaleDateString()}</span> : null}
              </div>
            </div>
            {pausable && (
              <Button
                size="sm"
                onClick={() => togglePause(ch)}
                disabled={busy === ch.id}
                className={`shrink-0 h-8 rounded-sm text-[11px] font-label ${ch.admin_paused ? "bg-[#34C759] hover:bg-[#16A34A] text-white" : "bg-[#EAB132] hover:bg-[#EAB132] text-black"}`}
                data-testid={`pause-btn-${ch.id}`}
              >
                {busy === ch.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : ch.admin_paused
                  ? <><Play className="w-3.5 h-3.5 mr-1" /> Resume</>
                  : <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</>}
              </Button>
            )}
          </div>
        );
      })}
      <p className="text-[11px] text-[#A1A1AA]">
        Pausing freezes live scoring — no new sales are tallied for the duel until resumed. Audit-logged.
      </p>
    </div>
  );
}

/* -------------------------------- AUDIT LOG ---------------------------------- */
function AuditLogSection({ company }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/admin/companies/${company.id}/audit-log?limit=50`)
      .then((r) => setRows(r.data.entries || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [company.id]);
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <ScrollText className="w-4 h-4 text-[#A1A1AA]" />
        <div className="font-label text-xs tracking-wider text-white/70">RECENT AUDIT EVENTS (last 50)</div>
      </div>
      {loading ? (
        <div className="text-center py-6 text-[#A1A1AA] text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 text-[#A1A1AA] text-sm">No audit entries yet.</div>
      ) : (
        <div className="space-y-1 max-h-80 overflow-y-auto pr-1" data-testid="audit-log-list">
          {rows.map((r, i) => (
            <div key={i} className="flex items-start gap-3 py-2 border-b border-[#27272A] last:border-0 text-xs">
              <span className="font-mono text-[10px] text-[#A1A1AA] whitespace-nowrap flex-shrink-0">
                {new Date(r.created_at).toLocaleString()}
              </span>
              <span className="font-semibold text-white truncate">{r.action}</span>
              <span className="text-[#A1A1AA] truncate flex-1">
                by {r.actor_name} ({r.actor_role})
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
